/**
 * Parse a free-text location input into { lat, lng }.
 *
 * Accepts:
 *  - Raw coordinates:  "-6.2088, 106.8456" | "-6.2088 106.8456" | "106.8456, -6.2088"
 *  - Google Maps URLs: /@lat,lng,zoom | ?q=lat,lng | !3d<lat>!4d<lng> | /place/lat,lng | ?ll=lat,lng
 *
 * Returns:
 *  - { lat, lng }            on success
 *  - { error: "<code>" }     when the input is recognisable but unusable
 *  - null                    when the input is empty
 *
 * Error codes: "short_link" | "no_coords_in_url" | "invalid" | "out_of_range".
 */

// Rough Indonesia bounding box — used only to disambiguate lat/lng order.
const ID = { minLat: -11.5, maxLat: 7.0, minLng: 94.0, maxLng: 142.0 };

const isLat = (v) => v >= -90 && v <= 90;
const isLng = (v) => v >= -180 && v <= 180;
const idLat = (v) => v >= ID.minLat && v <= ID.maxLat;
const idLng = (v) => v >= ID.minLng && v <= ID.maxLng;

/** Decide which of two raw numbers is latitude vs longitude. */
function orderRaw(a, b) {
  // 1) |value| > 90 cannot be a latitude.
  if (Math.abs(a) > 90 && Math.abs(b) <= 90) return { lat: b, lng: a };
  if (Math.abs(b) > 90 && Math.abs(a) <= 90) return { lat: a, lng: b };
  // 2) Indonesia disambiguation: lng ≈ 95–141, lat ≈ -11–7.
  if (idLng(a) && !idLat(a) && idLat(b)) return { lat: b, lng: a }; // "lng, lat"
  if (idLng(b) && !idLat(b) && idLat(a)) return { lat: a, lng: b }; // "lat, lng"
  // 3) Default to the standard "lat, lng" order.
  return { lat: a, lng: b };
}

function ok(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { error: "invalid" };
  if (!isLat(lat) || !isLng(lng)) return { error: "out_of_range" };
  return { lat, lng };
}

export function parseCoordinateInput(raw) {
  if (!raw || typeof raw !== "string") return null;
  const text = raw.trim();
  if (!text) return null;

  const looksLikeUrl =
    /^https?:\/\//i.test(text) ||
    /(google\.[a-z.]+\/maps|maps\.google|goo\.gl|maps\.app)/i.test(text);

  if (looksLikeUrl) {
    let url = text;
    try { url = decodeURIComponent(text); } catch { /* keep the encoded form */ }

    // Shortened links redirect server-side; they can't be resolved in the browser.
    if (/(maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(url)) return { error: "short_link" };

    // Google Maps always orders coordinates as lat,lng across every URL shape.
    const P = "(-?\\d+(?:\\.\\d+)?)";
    const m =
      url.match(new RegExp(`!3d${P}!4d${P}`)) ||                                          // place marker (most precise)
      url.match(new RegExp(`[?&](?:q|query|ll|center|daddr|destination)=${P}\\s*,\\s*${P}`, "i")) ||
      url.match(new RegExp(`/(?:place|dir|search)/${P}\\s*,\\s*${P}`, "i")) ||
      url.match(new RegExp(`@${P},${P}`));                                                // viewport centre (fallback)
    if (m) return ok(parseFloat(m[1]), parseFloat(m[2]));
    return { error: "no_coords_in_url" };
  }

  // Raw text: take the first two signed decimals and infer their order.
  const nums = text.match(/-?\d+(?:\.\d+)?/g);
  if (!nums || nums.length < 2) return { error: "invalid" };
  const { lat, lng } = orderRaw(parseFloat(nums[0]), parseFloat(nums[1]));
  return ok(lat, lng);
}
